import React, { useMemo, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MapPin, ExternalLink, Calendar, Briefcase } from 'lucide-react';
import { getJobTypeColor, formatJobDate, getJobPostedDate } from '../../utils/jobUtils'; // FIXED: Add the imports

// FIXED: Component to handle map updates when distance filter changes
const MapUpdater = ({ center, zoom, distanceFilter, userLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !userLocation) return;

    try {
      if (distanceFilter > 0) {
        // Calculate bounds manually instead of using L.circle
        const radiusInKm = distanceFilter * 1.60934; // Convert miles to km
        const earthRadiusKm = 6371;
        
        // Calculate approximate lat/lng offset for the radius
        const latOffset = (radiusInKm / earthRadiusKm) * (180 / Math.PI);
        const lngOffset = latOffset / Math.cos(userLocation.latitude * Math.PI / 180);
        
        // Create bounds
        const bounds = [
          [userLocation.latitude - latOffset, userLocation.longitude - lngOffset],
          [userLocation.latitude + latOffset, userLocation.longitude + lngOffset]
        ];
        
        // Fit the map to show the bounds with padding
        map.fitBounds(bounds, { padding: [50, 50] });
      } else {
        // No distance filter - use normal center/zoom
        map.setView(center, zoom);
      }
    } catch (error) {
      // Fallback to normal center/zoom
      map.setView(center, zoom);
    }
  }, [map, center, zoom, distanceFilter, userLocation]);

  return null;
};

// Custom icon creation functions
// FIXED: Completely rewrite icon creation to ensure colors show properly
const createJobIcon = (color, isBDJJob = false) => {
  const size = 12;
  const borderColor = isBDJJob ? '#ffffff' : '#000000'; // White for BDJ, Black for Practice
  const borderWidth = 2;
  
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background-color: ${color};
        border: ${borderWidth}px solid ${borderColor};
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      "></div>
    `,
    className: 'custom-job-icon',
    iconSize: [size + borderWidth * 2, size + borderWidth * 2],
    iconAnchor: [(size + borderWidth * 2) / 2, (size + borderWidth * 2) / 2],
  });
};

const createUserLocationIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        width: 16px;
        height: 16px;
        background-color: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      "></div>
    `,
    className: 'custom-user-icon',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

const JobMapView = ({ jobs, onViewJob, onSetViewMode, userLocation }) => {
  const { theme } = useTheme();
  
  // Add distance filter state
  const [distanceFilter, setDistanceFilter] = useState(0);
  
  // Listen for distance filter changes
  useEffect(() => {
    const handleDistanceFilterChange = (event) => {
      const newDistance = event.detail.distance;
      setDistanceFilter(newDistance);
    };

    window.addEventListener('distanceFilterChanged', handleDistanceFilterChange);
    
    // Check for global variable on mount
    if (window.currentDistanceFilter !== undefined) {
      setDistanceFilter(window.currentDistanceFilter);
    }

    return () => {
      window.removeEventListener('distanceFilterChanged', handleDistanceFilterChange);
    };
  }, []);

  // DON'T filter jobs by distance - show all valid jobs on map
  const validJobs = useMemo(() => {
    const filtered = jobs.filter(job => {
      // Must have valid coordinates
      if (!job.latitude || !job.longitude) {
        return false;
      }
      
      const lat = parseFloat(job.latitude);
      const lng = parseFloat(job.longitude);
      
      if (isNaN(lat) || isNaN(lng)) {
        return false;
      }
      
      // REMOVED: Distance filtering - let users explore the full map
      return true;
    });
    
    return filtered;
  }, [jobs, distanceFilter]);

  // Calculate map center and zoom - FIXED: Better logic for distance filtering
  const { center, zoom } = useMemo(() => {
    if (userLocation) {
      return {
        center: [userLocation.latitude, userLocation.longitude],
        zoom: distanceFilter > 0 ? 10 : 10 // Will be overridden by MapUpdater for distance filtering
      };
    }
    
    if (validJobs.length === 0) {
      // Default to UK center
      return {
        center: [54.5, -2.0],
        zoom: 6
      };
    }
    
    if (validJobs.length === 1) {
      return {
        center: [validJobs[0].latitude, validJobs[0].longitude],
        zoom: 12
      };
    }
    
    // Calculate bounds for multiple jobs
    const lats = validJobs.map(job => parseFloat(job.latitude));
    const lngs = validJobs.map(job => parseFloat(job.longitude));
    
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    
    return {
      center: [centerLat, centerLng],
      zoom: 8
    };
  }, [validJobs, userLocation, distanceFilter]);

  const createCustomMarker = (job) => {
    // SIMPLIFIED: No more inference logic - just use what's in the database
    const color = getJobTypeColor(job.employmentType);
    
    // Match legend styling - white border for RSS jobs, black border for practice jobs
    const borderColor = job.type === 'rss' ? '#ffffff' : '#000000';
    const borderWidth = 2;
    
    return L.divIcon({
      html: `
        <div style="
          width: 16px;
          height: 16px;
          background-color: ${color};
          border: ${borderWidth}px solid ${borderColor};
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        "></div>
      `,
      className: 'custom-marker',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });
  };

  return (
    <div className="relative h-[600px] w-full rounded-lg overflow-hidden z-[1]">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
        attributionControl={false}
        zoomControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* FIXED: Add MapUpdater component to handle zoom/center updates */}
        <MapUpdater 
          center={center} 
          zoom={zoom} 
          distanceFilter={distanceFilter} 
          userLocation={userLocation} 
        />
        
        {/* User location marker */}
        {userLocation && (
          <Marker
            position={[userLocation.latitude, userLocation.longitude]}
            icon={createUserLocationIcon()}
          >
            <Popup>
              <div className="text-center">
                <p className="font-semibold">Your Location</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* FIXED: Only show circle if we have valid userLocation and map is ready */}
        {userLocation && distanceFilter > 0 && (
          <Circle
            center={[userLocation.latitude, userLocation.longitude]}
            radius={distanceFilter * 1609.34} // Convert miles to meters
            pathOptions={{
              color: '#8B5CF6', // Purple color matching theme
              fillColor: '#8B5CF6',
              fillOpacity: 0.1,
              weight: 2,
              opacity: 0.6
            }}
          />
        )}

        {/* Job markers - FIXED: Ensure jobs are processed with normalized employment types */}
        {validJobs.map((job) => {
          // FIXED: Force employment type normalization for map display
          const normalizedEmploymentType = job.employmentType || 'Contract';
          const color = getJobTypeColor(normalizedEmploymentType);
          const isBDJJob = job.source === 'rss';
          
          return (
            <Marker
              key={`${job.source}-${job.id}`}
              position={[job.latitude, job.longitude]}
              icon={createCustomMarker(job)}
            >
              <Popup className="job-popup">
                <div className="min-w-[250px] p-2">
                  <div className="space-y-3">
                    {/* Job Title and Source */}
                    <div>
                      <h3 className="font-semibold text-lg mb-1 line-clamp-2">
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className={cn(
                            "text-xs",
                            // FIXED: Improved contrast for map popups
                            job.source === 'rss' || 
                            job.source === 'hygienist_therapist' || 
                            job.source === 'locum_dentist' || 
                            job.source === 'dental_nurse'
                              ? 'bg-blue-600 text-white border-blue-600' // FIXED: Always use high contrast in popups
                              : 'bg-green-600 text-white border-green-600'
                          )}
                        >
                          {/* FIXED: Add fallback logic for missing sourceLabel */}
                          {job.sourceLabel || (() => {
                            if (job.source === 'hygienist_therapist' || 
                                job.source === 'locum_dentist' || 
                                job.source === 'dental_nurse' || 
                                job.source === 'rss') {
                              return 'BDJ Jobs';
                            }
                            if (job.source === 'practice') {
                              return 'Practice';
                            }
                            if (job.originalUrl && job.originalUrl.includes('bdjjobs.com')) {
                              return 'BDJ Jobs';
                            }
                            return job.source || 'Unknown';
                          })()}
                        </Badge>
                        {job.distance !== null && job.distance !== undefined && (
                          <div className="flex items-center gap-1 text-xs text-purple-600">
                            <MapPin className="h-3 w-3" />
                            <span>{job.distance.toFixed(2)} miles</span> {/* FIXED: Added .toFixed(2) */}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Practice Info */}
                    {(job.practiceInfo || job.practiceNameAtPosting) && (
                      <p className="text-sm font-medium text-purple-700">
                        {typeof job.practiceInfo === 'string' 
                          ? job.practiceInfo 
                          : job.practiceNameAtPosting}
                      </p>
                    )}

                    {/* Location */}
                    {job.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>
                          {typeof job.location === 'string' 
                            ? job.location 
                            : job.location?.name || 'Location not specified'}
                          {job.postcode && ` (${job.postcode})`}
                        </span>
                      </div>
                    )}

                    {/* Employment Type and Salary - FIXED: Use normalized employment type */}
                    <div className="flex items-center text-sm text-gray-600">
                      <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>
                        {normalizedEmploymentType || 'Not specified'}
                        {job.salary && job.salary !== 'Competitive' && (
                          <span> • {job.salary}</span>
                        )}
                      </span>
                    </div>

                    {/* Posted date */}
                    <div className="flex items-center text-xs text-gray-500 mb-2">
                      <Calendar className="h-3 w-3 mr-1" />
                      Posted {formatJobDate(getJobPostedDate(job))} {/* FIXED: Use helper functions */}
                    </div>

                    {/* Description Preview */}
                    {job.description && (
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {job.description}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewJob(job)}
                        className="flex-1"
                        style={{
                          // FIXED: Force explicit colors for map popup button in dark mode
                          backgroundColor: '#ffffff',
                          borderColor: '#8B5CF6',
                          color: '#8B5CF6'
                        }}
                        onMouseEnter={(e) => {
                          // Force hover styles
                          e.target.style.backgroundColor = '#f3f4f6';
                          e.target.style.color = '#7C3AED';
                        }}
                        onMouseLeave={(e) => {
                          // Reset to normal styles
                          e.target.style.backgroundColor = '#ffffff';
                          e.target.style.color = '#8B5CF6';
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* No Jobs Message - Updated text */}
      {validJobs.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-[1001]">
          <div className={cn(
            "p-6 rounded-lg shadow-lg text-center max-w-sm",
            theme === 'light' ? 'bg-white' : 'bg-gray-900'
          )}>
            <MapPin className={cn(
              "h-12 w-12 mx-auto mb-4",
              theme === 'light' ? 'text-gray-400' : 'text-gray-500'
            )} />
            <h3 className={cn(
              "text-lg font-semibold mb-2",
              theme === 'light' ? 'text-gray-900' : 'text-white'
            )}>
              No jobs to display on map
            </h3>
            <p className={cn(
              "text-sm mb-4",
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            )}>
              No jobs have valid location data for mapping.
            </p>
            <Button
              onClick={() => onSetViewMode('list')}
              variant="outline"
              size="sm"
            >
              Switch to List View
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMapView;
